import { type Permission, type PermissionField } from '@/userManagement/domain/entities/Permission';
import { type Feature } from '@/userManagement/domain/entities/Feature';
import { type PermissionMatrixRow } from '../types/PermissionMatrixData';

interface PermissionDto {
  featureId: number;
  featureName: string;
  category: string;
  allowRead: boolean;
  allowWrite: boolean;
  allowDelete: boolean;
  allowCentralAccess: boolean;
  allowExcelExport: boolean;
  supportsRead?: boolean;
  supportsWrite?: boolean;
  supportsDelete?: boolean;
  supportsCentralAccess?: boolean;
  supportsExcelExport?: boolean;
  isActive?: boolean;
  isRemovedFromErp?: boolean;
}

function supportsFromFeature(
  f: Feature
): Pick<
  Permission,
  'supportsRead' | 'supportsWrite' | 'supportsDelete' | 'supportsCentralAccess' | 'supportsExcelExport' | 'isActive' | 'isRemovedFromErp'
> {
  return {
    supportsRead: f.supportsRead !== false,
    supportsWrite: f.supportsWrite !== false,
    supportsDelete: f.supportsDelete !== false,
    supportsCentralAccess: f.supportsCentralAccess !== false,
    supportsExcelExport: f.supportsExcelExport !== false,
    isActive: f.isActive !== false,
    isRemovedFromErp: f.isRemovedFromErp === true
  };
}

export function rowSupportsField(row: PermissionMatrixRow, field: PermissionField): boolean {
  switch (field) {
    case 'allowRead':
      return row.supportsRead !== false;
    case 'allowWrite':
      return row.supportsWrite !== false;
    case 'allowDelete':
      return row.supportsDelete !== false;
    case 'allowCentralAccess':
      return row.supportsCentralAccess !== false;
    case 'allowExcelExport':
      return row.supportsExcelExport !== false;
    default:
      return true;
  }
}

export function mapPermissionDtoToEntity(dto: PermissionDto): Permission {
  return {
    featureId: dto.featureId,
    featureName: dto.featureName,
    category: dto.category,
    allowRead: dto.allowRead,
    allowWrite: dto.allowWrite,
    allowDelete: dto.allowDelete,
    allowCentralAccess: dto.allowCentralAccess,
    allowExcelExport: dto.allowExcelExport,
    supportsRead: dto.supportsRead,
    supportsWrite: dto.supportsWrite,
    supportsDelete: dto.supportsDelete,
    supportsCentralAccess: dto.supportsCentralAccess,
    supportsExcelExport: dto.supportsExcelExport,
    isActive: dto.isActive,
    isRemovedFromErp: dto.isRemovedFromErp
  };
}

/** Maps GET `/roles/{roleId}/permissions` rows (camelCase or PascalCase) to domain {@link Permission}. */
export function mapPermissionWireRowToPermission(row: Record<string, unknown>): Permission | null {
  const featureId = Number(row.featureId ?? row.FeatureId ?? 0);
  if (!Number.isFinite(featureId) || featureId <= 0) return null;
  return mapPermissionDtoToEntity({
    featureId,
    featureName: String(row.featureName ?? row.FeatureName ?? ''),
    category: String(row.category ?? row.Category ?? ''),
    allowRead: Boolean(row.allowRead ?? row.AllowRead),
    allowWrite: Boolean(row.allowWrite ?? row.AllowWrite),
    allowDelete: Boolean(row.allowDelete ?? row.AllowDelete),
    allowCentralAccess: Boolean(row.allowCentralAccess ?? row.AllowCentralAccess),
    allowExcelExport: Boolean(row.allowExcelExport ?? row.AllowExcelExport),
    supportsRead: row.supportsRead === false || row.SupportsRead === false ? false : undefined,
    supportsWrite: row.supportsWrite === false || row.SupportsWrite === false ? false : undefined,
    supportsDelete: row.supportsDelete === false || row.SupportsDelete === false ? false : undefined,
    supportsCentralAccess: row.supportsCentralAccess === false || row.SupportsCentralAccess === false ? false : undefined,
    supportsExcelExport: row.supportsExcelExport === false || row.SupportsExcelExport === false ? false : undefined,
    isActive: row.isActive === false || row.IsActive === false ? false : undefined,
    isRemovedFromErp: row.isRemovedFromErp === true || row.IsRemovedFromErp === true ? true : undefined
  });
}

function flattenFeatures(features: Feature[]): Feature[] {
  return features.flatMap((f) => [f, ...flattenFeatures(f.children ?? [])]);
}

function compareMatrixLabel(aCategory: string, aName: string, bCategory: string, bName: string): number {
  const categoryCompare = aCategory.localeCompare(bCategory, undefined, { sensitivity: 'base' });
  if (categoryCompare !== 0) {
    return categoryCompare;
  }

  return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
}

/** Sorts catalog features for permission-matrix display (category, then feature name). TROC-207. */
export function sortFeaturesForPermissionMatrix(features: Feature[]): Feature[] {
  const uniqueById = new Map<number, Feature>();
  for (const feature of flattenFeatures(features)) {
    if (!uniqueById.has(feature.id)) {
      uniqueById.set(feature.id, feature);
    }
  }
  return [...uniqueById.values()]
    .filter((feature) => feature.isRemovedFromErp !== true && feature.isActive !== false)
    .sort((a, b) => compareMatrixLabel(a.category, a.name, b.category, b.name));
}

/** Sorts permission rows for matrix display (category, then feature name). TROC-207. */
export function sortPermissionsForMatrix(permissions: Permission[]): Permission[] {
  const uniqueByFeatureId = new Map<number, Permission>();
  for (const permission of permissions) {
    if (!uniqueByFeatureId.has(permission.featureId)) {
      uniqueByFeatureId.set(permission.featureId, permission);
    }
  }
  return [...uniqueByFeatureId.values()].sort((a, b) => compareMatrixLabel(a.category, a.featureName, b.category, b.featureName));
}

/** Deny-all rows for every known feature (used when a role has no template yet). */
export function buildDefaultPermissionsFromFeatures(features: Feature[]): Permission[] {
  return sortFeaturesForPermissionMatrix(features).map((f) => ({
    featureId: f.id,
    featureName: f.name,
    category: f.category,
    allowRead: false,
    allowWrite: false,
    allowDelete: false,
    allowCentralAccess: false,
    allowExcelExport: false,
    ...supportsFromFeature(f)
  }));
}

/** Matrix rows for the role editor: union of the feature catalog and the role's permission template.
 * Templated features keep their grant flag values; display name/category always come from the catalog
 * (TROC-349 — avoids stale spreadsheet aliases). Features present in the catalog but absent from the
 * template render as deny-all so admins can toggle them on. Template rows whose featureId is no longer
 * in the catalog (orphans) are appended at the end to avoid silent data loss. */
export function matrixRowsForRolePermissionTemplate(
  permissionTemplate: Permission[] | undefined | null,
  features: Feature[]
): PermissionMatrixRow[] {
  const flatCatalog = sortFeaturesForPermissionMatrix(features);
  const templateByFeatureId = new Map((permissionTemplate ?? []).map((p) => [p.featureId, p]));

  // Catalog order: each feature is either templated (template grant values; display name/category
  // always from the catalog so exports/UI match ERP FeatureName, not stale spreadsheet aliases)
  // or a deny-all default that the admin can re-enable by flag toggle.
  const merged: Permission[] = flatCatalog.map((f) => {
    const templated = templateByFeatureId.get(f.id);
    const supports = supportsFromFeature(f);
    if (templated) {
      return {
        ...templated,
        featureName: f.name,
        category: f.category,
        ...supports
      };
    }
    return {
      featureId: f.id,
      featureName: f.name,
      category: f.category,
      allowRead: false,
      allowWrite: false,
      allowDelete: false,
      allowCentralAccess: false,
      allowExcelExport: false,
      ...supports
    };
  });

  // Preserve any template orphans (FeatureIds not present in the catalog — defensive, normally
  // shouldn't happen, but avoids silently dropping a row admins might still need to manage).
  const catalogIds = new Set(flatCatalog.map((f) => f.id));
  const orphans = sortPermissionsForMatrix((permissionTemplate ?? []).filter((p) => !catalogIds.has(p.featureId)));

  return mapPermissionsToMatrixRows([...merged, ...orphans], null);
}

/** Matrix rows for the user permissions editor: user's effective permissions in catalog sort order. TROC-207. */
export function matrixRowsForUserPermissions(permissions: Permission[], features: Feature[]): PermissionMatrixRow[] {
  if (features.length === 0) {
    return mapPermissionsToMatrixRows(sortPermissionsForMatrix(permissions), null);
  }

  const flatCatalog = sortFeaturesForPermissionMatrix(features);
  const permissionByFeatureId = new Map(permissions.map((p) => [p.featureId, p]));
  const catalogIds = new Set(flatCatalog.map((f) => f.id));

  const ordered: Permission[] = flatCatalog
    .filter((f) => permissionByFeatureId.has(f.id))
    .map((f) => {
      const perm = permissionByFeatureId.get(f.id)!;
      return {
        ...perm,
        featureName: f.name,
        category: f.category,
        ...supportsFromFeature(f)
      };
    });

  const orphans = sortPermissionsForMatrix(permissions.filter((p) => !catalogIds.has(p.featureId)));
  return mapPermissionsToMatrixRows([...ordered, ...orphans], null);
}

/**
 * Baseline for the changed-rows filter: same feature rows as effective `permissions`, values from role template when present,
 * otherwise deny-all. Ensures snapshot keys match matrix rows (template-only features are ignored).
 */
export function permissionSnapshotBaseline(permissions: Permission[], roleTemplate: Permission[] | null): Permission[] {
  if (roleTemplate != null && roleTemplate.length > 0) {
    const templateMap = new Map(roleTemplate.map((t) => [t.featureId, t]));
    return permissions.map((p) => {
      const t = templateMap.get(p.featureId);
      if (t) {
        return {
          ...t,
          supportsRead: p.supportsRead,
          supportsWrite: p.supportsWrite,
          supportsDelete: p.supportsDelete,
          supportsCentralAccess: p.supportsCentralAccess,
          supportsExcelExport: p.supportsExcelExport
        };
      }
      return {
        ...p,
        allowRead: false,
        allowWrite: false,
        allowDelete: false,
        allowCentralAccess: false,
        allowExcelExport: false
      };
    });
  }
  return permissions.map((p) => ({
    ...p,
    allowRead: false,
    allowWrite: false,
    allowDelete: false,
    allowCentralAccess: false,
    allowExcelExport: false
  }));
}

export function mapPermissionsToMatrixRows(permissions: Permission[], template?: Permission[] | null): PermissionMatrixRow[] {
  const templateMap = new Map((template ?? []).map((t) => [t.featureId, t]));

  return permissions.map((perm) => {
    const tmpl = templateMap.get(perm.featureId);
    return {
      featureId: perm.featureId,
      featureName: perm.featureName,
      category: perm.category,
      parentId: null,
      allowRead: perm.allowRead,
      allowWrite: perm.allowWrite,
      allowDelete: perm.allowDelete,
      allowCentralAccess: perm.allowCentralAccess,
      allowExcelExport: perm.allowExcelExport,
      supportsRead: perm.supportsRead,
      supportsWrite: perm.supportsWrite,
      supportsDelete: perm.supportsDelete,
      supportsCentralAccess: perm.supportsCentralAccess,
      supportsExcelExport: perm.supportsExcelExport,
      isActive: perm.isActive,
      isRemovedFromErp: perm.isRemovedFromErp,
      ...(tmpl && {
        templateRead: tmpl.allowRead,
        templateWrite: tmpl.allowWrite,
        templateDelete: tmpl.allowDelete,
        templateCentralAccess: tmpl.allowCentralAccess,
        templateExcelExport: tmpl.allowExcelExport
      })
    };
  });
}
